import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material";
import moment, { Moment } from "moment";
import { doc, DocumentData, Firestore, QuerySnapshot } from "firebase/firestore";
import { useDocument } from "react-firebase-hooks/firestore";
import { useMemo } from "react";

interface ActivityData {
  id: string;
  activity: string;
  userId?: string;
  message?: string;
  timestamp: Moment;
}

interface CaseActivityTabProps {
  activity: QuerySnapshot<DocumentData>;
  firestore: Firestore;
}

interface UserNameProps {
  uid: string;
  firestore: Firestore;
}

const formatTimestamp = (timestamp: any): Moment => {
  if (timestamp?.toDate) {
    return moment(timestamp.toDate());
  }
  if (typeof timestamp === "string") {
    return moment(timestamp);
  }
  if (timestamp instanceof Date) {
    return moment(timestamp);
  }
  return moment();
};

const CaseActivityTab = ({ activity, firestore }: CaseActivityTabProps) => {
  const activities = useMemo(() => {
    if (!activity?.docs) return [];

    return activity.docs
      .map((doc): ActivityData => {
        const data = doc.data();
        return {
          id: doc.id,
          activity: data.activity || "Unknown Activity",
          userId: data.userId,
          message: data.message,
          timestamp: formatTimestamp(data.timestamp),
        };
      })
      .sort((a, b) => b.timestamp.diff(a.timestamp));
  }, [activity]);

  if (activities.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          No activities recorded yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {activities.map((activityItem) => (
        <Accordion key={activityItem.id} sx={{ mb: 1 }}>
          <AccordionSummary>
            <Box sx={{ width: "100%" }}>
              <Typography variant="h6" color="primary" gutterBottom>
                {activityItem.activity}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  User:
                </Typography>
                {activityItem.userId ? (
                  <UserName uid={activityItem.userId} firestore={firestore} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    System
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                Time: {activityItem.timestamp.format("MMM DD, YYYY [at] h:mm A")}
              </Typography>
            </Box>
          </AccordionSummary>
          {activityItem.message && (
            <AccordionDetails>
              <Typography variant="body1">
                <strong>Message:</strong> {activityItem.message}
              </Typography>
            </AccordionDetails>
          )}
        </Accordion>
      ))}
    </Box>
  );
};

const UserName = ({ uid, firestore }: UserNameProps) => {
  const [userDoc, loading, error] = useDocument(
    uid ? doc(firestore, "users", uid) : null
  );

  if (!uid) {
    return (
      <Typography variant="body2" color="text.secondary">
        Unknown
      </Typography>
    );
  }

  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading...
      </Typography>
    );
  }

  if (error || !userDoc?.exists()) {
    return (
      <Typography variant="body2" color="text.secondary">
        {uid}
      </Typography>
    );
  }

  const displayName = userDoc.get("name.display") || userDoc.get("email") || uid;

  return (
    <Typography variant="body2">
      {displayName}
    </Typography>
  );
};

export default CaseActivityTab;
